import React, { useState } from 'react';
import { X, Play, Coins, Shuffle, Users, Trophy, DoorOpen, AlertTriangle, Globe, Minus, Plus, Gamepad2, Eye } from 'lucide-react';
import type { RoomSettings, FriendSummary } from '../../shared/protocol';
import { MAX_PLAYERS_LIMIT, MIN_PLAYERS_LIMIT } from '../../shared/protocol';
import type { QuizSummary } from '../lib/api';
import { BRAND } from '../brand';
import { StartTimePicker, DEFAULT_START, startAtFromChoice, type StartChoice } from './StartTimePicker';
import { InviteFriendsPicker } from './InviteFriends';

interface Props {
  quiz: QuizSummary;
  balance: number;
  /** Solo la cuenta autorizada ve la opción de supervisar. */
  canMonitor?: boolean;
  onStart: (settings: Partial<RoomSettings>, startAt: number | null, inviteIds: string[]) => void;
  onClose: () => void;
  /** Amigos para invitar; null sin cuenta o mientras cargan. */
  friends: FriendSummary[] | null;
  myId: string;
  onManageFriends: () => void;
}

/** Botes sugeridos. El 0 va primero porque es, y debe ser, lo normal. */
const PRESETS = [0, 50, 200, 500];

export const HostSetupModal: React.FC<Props> = ({
  quiz,
  balance,
  canMonitor,
  onStart,
  onClose,
  friends,
  myId,
  onManageFriends,
}) => {
  const [start, setStart] = useState<StartChoice>(DEFAULT_START);
  const [inviteIds, setInviteIds] = useState<string[]>([]);
  const [settings, setSettings] = useState<RoomSettings>({
    gameMode: 'ffa',
    prizePool: 0,
    randomizeQuestions: false,
    randomizeOptions: true,
    showLeaderboardBetweenRounds: true,
    allowLateJoin: true,
    maxPlayers: 10,
    isPublic: true,
    hostPlays: false,
    monitorPlayers: false,
  });

  const [ack, setAck] = useState(false);
  const insufficient = settings.prizePool > balance;
  /** Con supervisión activa, el anfitrión debe aceptar sus obligaciones primero. */
  const needsAck = settings.monitorPlayers && !ack;

  const set = <K extends keyof RoomSettings>(key: K, value: RoomSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur-sm flex items-center-safe justify-center p-4 overflow-y-auto"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="host-title"
    >
      <div className="surface-raised w-full max-w-lg my-8 animate-pop">
        <div className="flex items-center gap-3 p-5 border-b border-ink-800">
          <span className="font-emoji text-3xl" aria-hidden="true">
            {quiz.coverEmoji}
          </span>
          <div className="flex-1 min-w-0">
            <h2 id="host-title" className="font-display font-bold text-lg truncate">
              {quiz.title}
            </h2>
            <p className="text-ink-500 text-xs">
              {quiz.questionCount} preguntas · hasta {settings.maxPlayers} jugadores
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Modo */}
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-ink-300 mb-2">Modo de juego</legend>
            <div className="grid grid-cols-2 gap-2">
              <ModeCard
                active={settings.gameMode === 'ffa'}
                onClick={() => set('gameMode', 'ffa')}
                icon={<Trophy className="w-5 h-5" />}
                title="Individual"
                description="Cada quien por su cuenta"
              />
              <ModeCard
                active={settings.gameMode === 'teams'}
                onClick={() => set('gameMode', 'teams')}
                icon={<Users className="w-5 h-5" />}
                title="Por equipos"
                description="Cuatro equipos de color"
              />
            </div>
          </fieldset>

          {/* Aforo */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="max-players" className="text-sm font-medium text-ink-300 flex items-center gap-1.5">
                <Users className="w-4 h-4 text-sky-400" />
                Jugadores
              </label>
              <span className="text-xs text-ink-500">sin contarte a ti</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => set('maxPlayers', Math.max(MIN_PLAYERS_LIMIT, settings.maxPlayers - 1))}
                disabled={settings.maxPlayers <= MIN_PLAYERS_LIMIT}
                aria-label="Menos jugadores"
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-ink-850 border border-ink-700
                           text-ink-300 hover:border-ink-600 disabled:opacity-40 transition-colors"
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                id="max-players"
                type="range"
                min={MIN_PLAYERS_LIMIT}
                max={MAX_PLAYERS_LIMIT}
                value={settings.maxPlayers}
                onChange={(e) => set('maxPlayers', Number(e.target.value))}
                className="flex-1 accent-sky-500"
              />
              <button
                type="button"
                onClick={() => set('maxPlayers', Math.min(MAX_PLAYERS_LIMIT, settings.maxPlayers + 1))}
                disabled={settings.maxPlayers >= MAX_PLAYERS_LIMIT}
                aria-label="Más jugadores"
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-ink-850 border border-ink-700
                           text-ink-300 hover:border-ink-600 disabled:opacity-40 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
              <span className="w-8 text-right font-mono font-bold text-lg text-ink-50 tabular-nums">
                {settings.maxPlayers}
              </span>
            </div>
          </div>

          {/* Bote */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-ink-300 flex items-center gap-1.5">
                <Coins className="w-4 h-4 text-ember-400" />
                Bote en {BRAND.currencyName}
              </span>
              <span className="text-xs text-ink-500">
                tienes {balance.toLocaleString('es')}
              </span>
            </div>

            <div className="flex gap-2">
              {PRESETS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => set('prizePool', amount)}
                  disabled={amount > balance}
                  className={`flex-1 py-2.5 rounded-lg font-semibold text-sm border-2 transition-colors ${
                    settings.prizePool === amount
                      ? 'bg-ember-500 border-ember-400 text-on-accent'
                      : 'bg-ink-850 border-ink-700 text-ink-300 hover:border-ink-600 disabled:opacity-40 disabled:cursor-not-allowed'
                  }`}
                >
                  {amount === 0 ? 'Sin bote' : amount}
                </button>
              ))}
            </div>

            {settings.prizePool > 0 && (
              <p className="text-ink-500 text-xs leading-relaxed">
                Se descuenta de tu saldo al abrir la sala y se reparte entre el podio
                (50 / 30 / 20 %). Si la partida no termina, se te devuelve entero.
              </p>
            )}

            {insufficient && (
              <p className="flex items-center gap-2 text-danger-400 text-sm">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                No te alcanzan los {BRAND.currencyName}.
              </p>
            )}
          </div>

          {/* Opciones */}
          <div className="space-y-1">
            <Toggle
              checked={settings.isPublic}
              onChange={(v) => set('isPublic', v)}
              icon={<Globe className="w-4 h-4" />}
              label="Anunciar en la sala de espera"
              hint="Cualquiera podrá verla en la portada y unirse. Si no, solo con el PIN."
            />
            <Toggle
              checked={settings.randomizeQuestions}
              onChange={(v) => set('randomizeQuestions', v)}
              icon={<Shuffle className="w-4 h-4" />}
              label="Barajar preguntas"
              hint="Distinto orden en cada partida"
            />
            <Toggle
              checked={settings.randomizeOptions}
              onChange={(v) => set('randomizeOptions', v)}
              icon={<Shuffle className="w-4 h-4" />}
              label="Barajar opciones"
              hint="Dificulta copiar la posición de la respuesta"
            />
            <Toggle
              checked={settings.showLeaderboardBetweenRounds}
              onChange={(v) => set('showLeaderboardBetweenRounds', v)}
              icon={<Trophy className="w-4 h-4" />}
              label="Clasificación entre rondas"
            />
            <Toggle
              checked={settings.allowLateJoin}
              onChange={(v) => set('allowLateJoin', v)}
              icon={<DoorOpen className="w-4 h-4" />}
              label="Permitir entrar tarde"
              hint="Quien llegue a mitad empieza desde cero"
            />
            <Toggle
              checked={settings.hostPlays}
              onChange={(v) => set('hostPlays', v)}
              icon={<Gamepad2 className="w-4 h-4" />}
              label="Jugar yo también"
              hint={
                settings.prizePool > 0
                  ? 'Compites por el podio y por tu propio bote; con bote, la partida cuenta para tu D-Rating. La sala avisa a todos de que juegas'
                  : 'Contestas y compites por el podio. La sala avisa a todos de que juegas'
              }
            />
            {canMonitor && (
              <Toggle
                checked={settings.monitorPlayers}
                onChange={(v) => set('monitorPlayers', v)}
                icon={<Eye className="w-4 h-4" />}
                label="Supervisar y grabar pantallas"
                hint="Ves en vivo lo que hace cada jugador en Open Dahu y queda grabado 30 días. Cada jugador debe aceptar antes de que se le grabe."
              />
            )}
            {canMonitor && settings.monitorPlayers && (
              <label className="flex items-start gap-3 rounded-xl border border-ember-600/60 bg-ember-500/5 p-3.5 cursor-pointer mt-1">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-ember-500"
                />
                <span className="text-xs text-ink-300 leading-relaxed">
                  Me hago responsable de esta supervisión: tengo derecho a hacerla (si hay menores,
                  el permiso de sus tutores o del centro), usaré las grabaciones solo para detectar
                  trampas en esta partida, no las compartiré ni las publicaré, y atenderé a quien
                  pida que se borren. Entiendo que a las cuentas de menores de 18 no se les graba y que
                  quien no acepte no podrá jugar en la sala.
                </span>
              </label>
            )}
          </div>

          <div className="h-px bg-ink-800" />

          <StartTimePicker
            value={start}
            onChange={setStart}
            manualLabel="La empiezo yo"
            manualHint="Abres la sala y pulsas «Empezar» cuando quieras."
          />

          <div className="h-px bg-ink-800" />

          <div className="space-y-2">
            <p className="text-sm font-medium text-ink-300">Invitar a amigos a unirse</p>
            <InviteFriendsPicker
              friends={friends}
              selected={inviteIds}
              onChange={setInviteIds}
              exclude={[myId]}
              onManageFriends={onManageFriends}
            />
          </div>

          <button
            onClick={() => onStart(settings, startAtFromChoice(start), inviteIds)}
            disabled={insufficient || needsAck}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
          >
            <Play className="w-5 h-5" fill="currentColor" />
            Abrir sala
          </button>
        </div>
      </div>
    </div>
  );
};

const ModeCard: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ active, onClick, icon, title, description }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`p-4 rounded-xl border-2 text-left transition-colors ${
      active
        ? 'bg-sky-600/15 border-sky-500'
        : 'bg-ink-850 border-ink-700 hover:border-ink-600'
    }`}
  >
    <span className={active ? 'text-sky-400' : 'text-ink-400'}>{icon}</span>
    <p className="font-semibold text-ink-50 mt-1.5">{title}</p>
    <p className="text-ink-500 text-xs">{description}</p>
  </button>
);

const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  icon: React.ReactNode;
  label: string;
  hint?: string;
}> = ({ checked, onChange, icon, label, hint }) => (
  <label className="flex items-center gap-3 py-2.5 cursor-pointer group">
    <span className={checked ? 'text-sky-400' : 'text-ink-600'}>{icon}</span>
    <span className="flex-1">
      <span className="block text-sm text-ink-100 font-medium">{label}</span>
      {hint && <span className="block text-xs text-ink-500">{hint}</span>}
    </span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only peer"
    />
    <span
      aria-hidden="true"
      className={`w-11 h-6 rounded-full p-0.5 transition-colors shrink-0 ${
        checked ? 'bg-sky-600' : 'bg-ink-700'
      } peer-focus-visible:ring-2 peer-focus-visible:ring-sky-400 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-ink-900`}
    >
      <span
        className={`block w-5 h-5 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-5' : ''
        }`}
      />
    </span>
  </label>
);
